import type { CaptureResult, PostHog, Properties } from "posthog-js";
import { routeTemplate } from "./route";

// Product analytics for the Artie workspace (spec: docs/superpowers/specs/
// 2026-10-14-chat-ui-ui-tagging-design.md). Artie has its own PostHog project
// (D1); without a key nothing is loaded or sent. Only named events and
// $pageleave are sent: autocapture, automatic pageviews and recordings stay off (D3).

export interface AnalyticsConfig {
  key?: string;
  host?: string;
}

/** The fields of the signed-in user analytics may use. Never email or name (D4). */
export interface AnalyticsUser {
  id: number | string;
  company_id?: number | string | null;
  roles?: string[];
  is_platform_admin?: boolean;
}

const DEFAULT_HOST = "https://us.i.posthog.com";

let ph: PostHog | null = null;
let current: string | null = null; // "user|company" the client is started for
let generation = 0; // bumped by stop(), so a start() still loading loses to it
// Events sent while posthog-js is still loading (the first pageview always
// is), sent once the person is identified. Bounded so a stuck load can't grow it.
let queue: Array<[string, Properties]> | null = null;
const QUEUE_LIMIT = 50;
let allowed = import.meta.env.MODE !== "test";
let load = (): Promise<{ default: PostHog }> => import("posthog-js");

/** Starts PostHog for this user, or switches to them. A no-op without a key. */
export async function start(cfg: AnalyticsConfig, user: AnalyticsUser): Promise<void> {
  const key = (cfg.key ?? "").trim();
  if (!allowed || !key || user.id == null) return;

  const run = generation;
  if (!ph) {
    queue ??= [];
    let client: PostHog;
    try {
      client = (await load()).default;
      if (run !== generation) return;
      client.init(key, {
        api_host: (cfg.host ?? "").trim() || DEFAULT_HOST,
        person_profiles: "identified_only",
        autocapture: false,
        capture_pageview: false,
        // Sent on tab close / leaving Artie, so a session ends when the
        // person left rather than at their last click. Best-effort.
        capture_pageleave: true,
        disable_session_recording: true,
        disable_surveys: true,
        // Explicitly off: left unset, PostHog's project settings could turn
        // them on remotely, and exceptions carry free text (D3).
        capture_exceptions: false,
        capture_heatmaps: false,
        capture_dead_clicks: false,
        capture_performance: false,
        advanced_disable_flags: true,
        persistence: "localStorage",
        before_send: scrubUrls,
      });
    } catch (err) {
      // Blocked, offline or a broken init: analytics stays off, the app
      // carries on, and the next start() tries again.
      console.warn("[analytics] posthog-js failed to start", err);
      if (run === generation) queue = null;
      return;
    }
    ph = client;
  }

  const company = user.company_id == null ? null : String(user.company_id);
  const who = `${user.id}|${company ?? ""}`;
  if (who === current) return;
  // A different person or company on the same browser must not inherit the
  // previous one's identity or session.
  if (current) ph.reset();
  current = who;
  // Registered on every switch, because reset() clears super properties.
  ph.register({ product: "artie", platform: "web" });
  ph.identify(String(user.id), { roles: user.roles ?? [], is_platform_admin: !!user.is_platform_admin });
  if (company) ph.group("company", company);
  flush();
}

function flush(): void {
  const pending = queue ?? [];
  queue = null;
  for (const [event, props] of pending) ph?.capture(event, props);
}

/** Sign-out or leaving the workspace: forget the person. */
export function stop(): void {
  generation += 1;
  queue = null;
  if (ph && current) ph.reset();
  current = null;
}

/** Sends a named event, or queues it while start() is loading. Otherwise a no-op until someone is identified. */
export function track(event: string, props: Properties = {}): void {
  if (queue) {
    if (queue.length < QUEUE_LIMIT) queue.push([event, props]);
    return;
  }
  if (!ph || !current) return;
  ph.capture(event, props);
}

function scrubUrl(value: string): string {
  try {
    const url = new URL(value);
    // Another site's path (a search, a webmail thread) is none of our business.
    if (url.origin !== window.location.origin) return url.origin;
    return url.origin + routeTemplate(url.pathname);
  } catch {
    return value;
  }
}

function scrubValue(key: string, value: unknown, depth: number): unknown {
  if (typeof value === "string") {
    if (/^https?:\/\//i.test(value)) return scrubUrl(value);
    if (key.endsWith("pathname") && value.startsWith("/")) return routeTemplate(value);
    return value;
  }
  if (depth > 0 && value && typeof value === "object" && !Array.isArray(value)) {
    return scrubProps(value as Properties, depth - 1);
  }
  return value;
}

function scrubProps(props: Properties, depth = 2): Properties {
  const out: Properties = {};
  for (const [k, v] of Object.entries(props)) out[k] = scrubValue(k, v, depth);
  return out;
}

/**
 * posthog-js `before_send`: every url and path in an event is cut down to its
 * route template, so record ids and ?session= never leave the browser (D5).
 * Matched by value rather than by property name, because posthog-js adds url
 * properties under many names ($current_url, $session_entry_url, $initial_…).
 */
export function scrubUrls(cr: CaptureResult): CaptureResult {
  return {
    ...cr,
    properties: scrubProps(cr.properties ?? {}),
    ...(cr.$set && { $set: scrubProps(cr.$set) }),
    ...(cr.$set_once && { $set_once: scrubProps(cr.$set_once) }),
  };
}

export function __resetForTests(
  allow = false,
  loader: () => Promise<{ default: PostHog }> = () => import("posthog-js"),
): void {
  load = loader;
  ph = null;
  current = null;
  generation = 0;
  queue = null;
  allowed = allow;
}
